import React, { useState } from 'react';
import { modals } from '@mantine/modals';
import {
  Button,
  Collapse,
  Group,
  LoadingOverlay,
  Modal,
  SegmentedControl,
  Text,
} from '@mantine/core';
import {
  IconCalendar,
  IconChevronDown,
  IconCopy,
  IconEdit,
  IconTrash,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import useParkingService from '../../../hooks/parking-services/useParkingService.ts';
import useUpdateParkingServiceParkingRates from '../../../hooks/parking-services/useUpdateParkingServiceParkingRates.ts';
import {
  AdditionalRules,
  MaximumFeeDetail,
  RegularRule,
  SpecificRule,
} from '../../../models/ParkingRate.ts';
import { DayOfWeek } from '../../../models/OperationHours.ts';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import { notifyError } from '../../../utils/NotifyError.tsx';
import {
  ALL_DOWS,
  DOW_SHORT,
  DOW_ZH,
  DaySlot,
  MaxFeeSlot,
  buildRegularDayCards,
  buildSpecificDayCards,
  dateToShortLabel,
  parseRate,
} from '../../../utils/ParkingRateCardUtils.ts';
import { useTheme } from '../../../context/ThemeProvider.tsx';

type FilterType = 'all' | 'regular' | 'specific';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** "YYYY-MM-DDTHH:mm:ss" → "YYYY/MM/DD" */
const formatDate = (dt: string): string => {
  const d = dt.split('T')[0];
  return d.replace(/-/g, '/');
};
/** 處理 進場緩衝時間 與 跨日重新計算 的轉譯 */
function formatAdditionalRules(rules: AdditionalRules): string {
  const parts: string[] = [];
  if (rules.entryBufferTime !== null)
    parts.push(`進場緩衝 ${rules.entryBufferTime} 分鐘`);
  if (rules.isResetEveryDay) parts.push('跨日重新計算');
  return parts.join('\n') || '-';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** 星期幾圈圈列（一般費率用） */
const DowChips: React.FC<{ dows: DayOfWeek[] }> = ({ dows }) => {
  const activeDows = new Set(dows);
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {ALL_DOWS.map((d) => {
        const active = activeDows.has(d);
        return (
          <span
            key={d}
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 600,
              background: active ? '#eff6ff' : '#f3f4f6',
              color: active ? '#2563eb' : '#9ca3af',
              border: active
                ? '1.5px solid #bfdbfe'
                : '1.5px solid transparent',
            }}
          >
            {DOW_SHORT[d]}
          </span>
        );
      })}
    </div>
  );
};

/** 單一時段的費率顯示（免費／單次／累進，皆從 slot.rate 解析） */
const RateDisplay: React.FC<{
  slot: DaySlot;
  isActiveSlot: boolean;
  isActiveRule: boolean;
}> = ({ slot, isActiveSlot, isActiveRule }) => {
  const { colorScheme } = useTheme();

  if (slot.rate === null) {
    return (
      <Text
        style={{
          fontSize: 12,
          color: isActiveSlot ? '#991b1b' : '#b0b8c4',
          fontStyle: 'italic',
        }}
      >
        未知
      </Text>
    );
  }
  const feeColor =
    isActiveSlot && isActiveRule
      ? '#15803d'
      : colorScheme === 'light'
        ? 'black'
        : 'white';
  const parsed = parseRate(slot.rate);
  if (parsed.kind === 'free') {
    return (
      <Text
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: feeColor,
          marginLeft: 2,
        }}
      >
        免費
      </Text>
    );
  }
  if (parsed.kind === 'per_entry') {
    return (
      <Group gap={4}>
        <Text
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: feeColor,
            marginLeft: 2,
          }}
        >
          {parsed.fee}
        </Text>
        <Text
          style={{
            fontSize: 11,
            fontWeight: 400,
            color: '#9e9e9e',
            marginLeft: 2,
          }}
        >
          元 / 次
        </Text>
      </Group>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'flex-end',
      }}
    >
      {parsed.tiers.map((t, i) => (
        <Group key={i} gap={4} wrap="nowrap">
          {t.timeLabel && (
            <Text
              style={{
                fontSize: 11,
                fontWeight: 400,
                color: '#9e9e9e',
                whiteSpace: 'nowrap',
              }}
            >
              {t.timeLabel}
            </Text>
          )}
          {t.isFree ? (
            <Text style={{ fontSize: 14, fontWeight: 700, color: feeColor }}>
              免費
            </Text>
          ) : (
            <>
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: '#9e9e9e',
                  whiteSpace: 'nowrap',
                }}
              >
                每 {t.intervalMinutes} 分鐘
              </Text>
              <Text style={{ fontSize: 14, fontWeight: 700, color: feeColor }}>
                {t.fee}
              </Text>
              <Text style={{ fontSize: 11, fontWeight: 400, color: '#9e9e9e' }}>
                元
              </Text>
            </>
          )}
        </Group>
      ))}
    </div>
  );
};

/** 單一時段的最高收費顯示 */
const MaxFeeDisplay: React.FC<{
  maxFeeSlot: MaxFeeSlot;
  isActive: boolean;
}> = ({ maxFeeSlot, isActive }) => {
  const { colorScheme } = useTheme();
  const { maximumFee } = maxFeeSlot;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'flex-end',
      }}
    >
      {maximumFee.map((fee, i) => {
        const label =
          fee.type === 'WHOLE_DAY'
            ? '全天最高'
            : fee.type === 'FIRST_UNIT'
              ? `前 ${(fee as Extract<MaximumFeeDetail, { type: 'FIRST_UNIT' }>).intervalMinutes} 分鐘最高`
              : `每 ${(fee as Extract<MaximumFeeDetail, { type: 'PER_UNIT' }>).intervalMinutes} 分鐘最高`;
        return (
          <Group key={i} gap={4} wrap="nowrap">
            <Text
              style={{
                fontSize: 11,
                color: '#9e9e9e',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Text>
            <Text
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: isActive
                  ? '#15803d'
                  : colorScheme === 'light'
                    ? 'black'
                    : 'white',
              }}
            >
              {fee.fee}
            </Text>
            <Text style={{ fontSize: 11, color: '#9e9e9e' }}>元</Text>
          </Group>
        );
      })}
    </div>
  );
};

/** 一般費率：代表「某幾個星期幾」全天時段的卡片 */
const RegularRatePeriodCard: React.FC<{
  dows: DayOfWeek[];
  slots: DaySlot[];
  maxFeeSlots: MaxFeeSlot[];
  isToday?: boolean;
  isActiveRule?: boolean;
  activeSlotIndex?: number;
  activeMaxFeeIndex?: number;
}> = ({
  dows,
  slots,
  maxFeeSlots,
  isToday = false,
  isActiveRule = false,
  activeSlotIndex = -1,
  activeMaxFeeIndex = -1,
}) => {
  const { colorScheme } = useTheme();

  return (
    <div
      style={{
        border:
          isToday && isActiveRule
            ? '5px solid #9bc7ff'
            : colorScheme === 'light'
              ? '1.5px solid #bfdbfe'
              : '1.5px solid #737373ff',
        borderRadius: 10,
        padding: '12px 16px',
        position: 'relative',
      }}
    >
      <DowChips dows={dows} />
      <div
        style={{
          marginTop: 10,
          borderTop: `1px solid ${isToday ? '#93c5fd' : '#bfdbfe'}`,
          paddingTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {slots.map((slot, i) => {
          const isGap = slot.rate === null;
          const isActiveSlot = i === activeSlotIndex;
          const bg =
            isActiveSlot && isGap
              ? '#fee2e2'
              : isActiveSlot && isActiveRule
                ? '#dcfce7'
                : isGap
                  ? '#f3f4f6'
                  : undefined;
          const timeColor =
            isActiveSlot && isGap
              ? '#991b1b'
              : isActiveSlot && isActiveRule
                ? '#15803d'
                : isGap
                  ? '#b0b8c4'
                  : '#6b7280';
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                background: bg,
                borderRadius: isActiveSlot || isGap ? 6 : undefined,
                padding: isActiveSlot || isGap ? '3px 6px' : '1px 0',
                margin: isActiveSlot || isGap ? '0 -6px' : undefined,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: timeColor,
                  fontWeight: isActiveRule && isActiveSlot ? 700 : 400,
                  fontStyle: isGap ? 'italic' : undefined,
                  display: 'flex',
                  gap: 4,
                }}
              >
                {slot.startTime} – {slot.endTime}
              </span>
              <RateDisplay
                slot={slot}
                isActiveSlot={isActiveSlot}
                isActiveRule={isActiveRule}
              />
            </div>
          );
        })}
      </div>
      {maxFeeSlots.length > 0 && (
        <div
          style={{
            marginTop: 8,
            borderTop: `1px solid ${isToday ? '#93c5fd' : '#bfdbfe'}`,
            paddingTop: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <span style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>
            最高收費
          </span>
          {maxFeeSlots.map((maxFeeSlot, i) => {
            const isActiveMaxFee = i === activeMaxFeeIndex && isActiveRule;
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  background: isActiveMaxFee ? '#dcfce7' : undefined,
                  borderRadius: isActiveMaxFee ? 6 : undefined,
                  padding: isActiveMaxFee ? '3px 6px' : '1px 0',
                  margin: isActiveMaxFee ? '0 -6px' : undefined,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: isActiveMaxFee ? '#15803d' : '#6b7280',
                    fontWeight: isActiveMaxFee ? 700 : 400,
                  }}
                >
                  {maxFeeSlot.startTime} – {maxFeeSlot.endTime}
                </span>
                <MaxFeeDisplay
                  maxFeeSlot={maxFeeSlot}
                  isActive={isActiveMaxFee}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** 日期 chips 列（特殊費率用） */
const DateChips: React.FC<{ dates: string[] }> = ({ dates }) => (
  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
    {dates.map((d) => {
      const dow = new Date(d + 'T12:00:00').getDay();
      return (
        <span
          key={d}
          style={{
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            background: '#fff7ed',
            color: '#d97706',
            border: '1.5px solid #fde68a',
            whiteSpace: 'nowrap',
          }}
        >
          {dateToShortLabel(d)} ({DOW_ZH[dow]})
        </span>
      );
    })}
  </div>
);

/** 特殊費率：代表「某幾天」全天時段的卡片 */
const SpecificRatePeriodCard: React.FC<{
  dates: string[]; // YYYY-MM-DD
  slots: DaySlot[];
  maxFeeSlots: MaxFeeSlot[];
  isToday?: boolean;
  isActiveRule?: boolean;
  activeSlotIndex?: number;
  activeMaxFeeIndex?: number;
}> = ({
  dates,
  slots,
  maxFeeSlots,
  isToday = false,
  isActiveRule = false,
  activeSlotIndex = -1,
  activeMaxFeeIndex = -1,
}) => {
  const { colorScheme } = useTheme();

  return (
    <div
      style={{
        border:
          isToday && isActiveRule
            ? '5px solid #fde68a'
            : colorScheme === 'light'
              ? '1.5px solid #fde68a'
              : '1.5px solid #737373ff',
        borderRadius: 10,
        padding: '12px 16px',
        position: 'relative',
      }}
    >
      <DateChips dates={dates} />
      <div
        style={{
          marginTop: 10,
          borderTop: `1px solid ${isToday ? '#fbbf24' : '#fde68a'}`,
          paddingTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {slots.map((slot, i) => {
          const isGap = slot.rate === null;
          const isActiveSlot = i === activeSlotIndex;
          const bg =
            isActiveSlot && isGap
              ? '#fee2e2'
              : isActiveSlot && isActiveRule
                ? '#dcfce7'
                : isGap
                  ? '#f3f4f6'
                  : undefined;
          const timeColor =
            isActiveSlot && isGap
              ? '#991b1b'
              : isActiveSlot && isActiveRule
                ? '#15803d'
                : isGap
                  ? '#b0b8c4'
                  : '#6b7280';
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                background: bg,
                borderRadius: isActiveSlot || isGap ? 6 : undefined,
                padding: isActiveSlot || isGap ? '3px 6px' : '1px 0',
                margin: isActiveSlot || isGap ? '0 -6px' : undefined,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: timeColor,
                  fontWeight: isActiveSlot ? 700 : 400,
                  fontStyle: isGap ? 'italic' : undefined,
                  display: 'flex',
                  gap: 4,
                }}
              >
                {slot.startTime} – {slot.endTime}
              </span>
              <RateDisplay
                slot={slot}
                isActiveSlot={isActiveSlot}
                isActiveRule={isActiveRule}
              />
            </div>
          );
        })}
      </div>
      {maxFeeSlots.length > 0 && (
        <div
          style={{
            marginTop: 8,
            borderTop: `1px solid ${isToday ? '#fbbf24' : '#fde68a'}`,
            paddingTop: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <span style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>
            最高收費
          </span>
          {maxFeeSlots.map((maxFeeSlot, i) => {
            const isActiveMaxFee = i === activeMaxFeeIndex && isActiveRule;
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  background: isActiveMaxFee ? '#dcfce7' : undefined,
                  borderRadius: isActiveMaxFee ? 6 : undefined,
                  padding: isActiveMaxFee ? '3px 6px' : '1px 0',
                  margin: isActiveMaxFee ? '0 -6px' : undefined,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: isActiveMaxFee ? '#15803d' : '#6b7280',
                    fontWeight: isActiveMaxFee ? 700 : 400,
                  }}
                >
                  {maxFeeSlot.startTime} – {maxFeeSlot.endTime}
                </span>
                <MaxFeeDisplay
                  maxFeeSlot={maxFeeSlot}
                  isActive={isActiveMaxFee}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** 是否是現在套用的費率規則 */
const isRuleActive = (rule: RegularRule | SpecificRule) => {
  const now = new Date();

  return (
    new Date(rule.effectivePeriod.startDateTime) <= now &&
    new Date(rule.effectivePeriod.endDateTime) >= now
  );
};

/** 一筆 RegularRule 的卡片（可收合） */
const RegularRuleCard: React.FC<{
  isCurrentlyActive: boolean;
  rule: RegularRule;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
}> = ({ isCurrentlyActive, rule, onEdit, onCopy, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const start = formatDate(rule.effectivePeriod.startDateTime);
  const end = formatDate(rule.effectivePeriod.endDateTime);

  const { colorScheme } = useTheme();

  return (
    <div
      style={{
        border: isCurrentlyActive
          ? '5px solid #bfdbfe'
          : colorScheme === 'light'
            ? '1px solid #f3f4f6'
            : '1px solid #737373ff',
        backgroundColor: isCurrentlyActive
          ? 'rgba(191,219,254,0.08)'
          : undefined,
        borderRadius: 12,
        overflow: 'hidden',
        marginBottom: 16,
      }}
    >
      {/* Header（點擊收合／展開） */}
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '14px 18px',
          backgroundColor: isCurrentlyActive
            ? 'rgba(191,219,254,0.2)'
            : colorScheme === 'light'
              ? '#F8F9FA'
              : '#303030',
          gap: 10,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: '#eff6ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconCalendar size={16} color={'#4ca1ff'} />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: colorScheme === 'light' ? '#111' : 'white',
            }}
          >
            一般費率 ｜ {rule.name}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>
            {start} - {end}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            style={{
              width: 32,
              height: 32,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              backgroundColor:
                colorScheme === 'light' ? '#fff' : 'rgba(107,114,128,0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
            }}
          >
            <IconEdit size={15} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopy();
            }}
            style={{
              width: 32,
              height: 32,
              border: '1px solid #bbf7d0',
              borderRadius: 8,
              backgroundColor:
                colorScheme === 'light' ? '#fff' : 'rgba(0,255,94,0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#16a34a',
            }}
          >
            <IconCopy size={15} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              width: 32,
              height: 32,
              border: '1px solid #fca5a5',
              borderRadius: 8,
              background:
                colorScheme === 'light' ? '#fff' : 'rgba(255,0,0,0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ef4444',
            }}
          >
            <IconTrash size={15} />
          </button>
          <IconChevronDown
            size={16}
            color="#9ca3af"
            style={{
              flexShrink: 0,
              transition: 'transform 0.2s ease',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
          />
        </div>
      </div>

      {/* 可收合內容 */}
      <Collapse expanded={expanded}>
        <div
          style={{
            borderTop:
              colorScheme === 'light'
                ? '1px solid #f3f4f6'
                : '1px solid #737373ff',
            padding: '14px 18px',
          }}
        >
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
            各時段費率
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(max-content, 1fr))',
              gap: 10,
              overflowX: 'auto',
            }}
          >
            {(() => {
              const now = new Date();
              // ALL_DOWS: Mon=0…Sun=6；JS getDay(): 0=Sun…6=Sat
              const todayDow = ALL_DOWS[(now.getDay() + 6) % 7];
              const nowTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
              return buildRegularDayCards(rule).map((card, i) => {
                const isToday = card.dows.includes(todayDow);
                const activeSlotIndex = isToday
                  ? card.slots.findIndex(
                      (s) => s.startTime <= nowTime && nowTime <= s.endTime,
                    )
                  : -1;
                const activeMaxFeeIndex = isToday
                  ? card.maxFeeSlots.findIndex(
                      (s) => s.startTime <= nowTime && nowTime <= s.endTime,
                    )
                  : -1;
                return (
                  <RegularRatePeriodCard
                    key={i}
                    dows={card.dows}
                    slots={card.slots}
                    maxFeeSlots={card.maxFeeSlots}
                    isToday={isToday}
                    isActiveRule={isCurrentlyActive}
                    activeSlotIndex={activeSlotIndex}
                    activeMaxFeeIndex={activeMaxFeeIndex}
                  />
                );
              });
            })()}
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
              進階費率資訊
            </div>
            <div
              style={{
                backgroundColor:
                  colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: colorScheme === 'light' ? '#374151' : 'white',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}
            >
              {formatAdditionalRules(rule.additionalRules)}
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
              費率備註
            </div>
            <div
              style={{
                backgroundColor:
                  colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: colorScheme === 'light' ? '#374151' : 'white',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}
            >
              {rule.description || '-'}
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

/** 一筆 SpecificRule 的卡片（可收合） */
const SpecificRuleCard: React.FC<{
  isCurrentlyActive: boolean;
  rule: SpecificRule;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
}> = ({ isCurrentlyActive, rule, onEdit, onCopy, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const start = formatDate(rule.effectivePeriod.startDateTime);
  const end = formatDate(rule.effectivePeriod.endDateTime);

  const { colorScheme } = useTheme();

  return (
    <div
      style={{
        border: isCurrentlyActive
          ? '5px solid #fde68a'
          : colorScheme === 'light'
            ? '1px solid #f3f4f6'
            : '1px solid #737373ff',
        backgroundColor: isCurrentlyActive
          ? 'rgba(253,230,138,0.08)'
          : undefined,
        borderRadius: 12,
        overflow: 'hidden',
        marginBottom: 16,
      }}
    >
      {/* Header（點擊收合／展開） */}
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '14px 18px',
          backgroundColor: isCurrentlyActive
            ? 'rgba(253,230,138,0.2)'
            : colorScheme === 'light'
              ? '#F8F9FA'
              : '#303030',
          gap: 10,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: '#fff7ed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconCalendar size={16} color={'#faa353'} />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: colorScheme === 'light' ? '#111' : 'white',
            }}
          >
            特殊費率｜{rule.name}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>
            {start} - {end}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            style={{
              width: 32,
              height: 32,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              backgroundColor:
                colorScheme === 'light' ? '#fff' : 'rgba(107,114,128,0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
            }}
          >
            <IconEdit size={15} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopy();
            }}
            style={{
              width: 32,
              height: 32,
              border: '1px solid #bbf7d0',
              borderRadius: 8,
              backgroundColor:
                colorScheme === 'light' ? '#fff' : 'rgba(107,114,128,0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#16a34a',
            }}
          >
            <IconCopy size={15} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              width: 32,
              height: 32,
              border: '1px solid #fca5a5',
              borderRadius: 8,
              backgroundColor:
                colorScheme === 'light' ? '#fff' : 'rgba(255,0,0,0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ef4444',
            }}
          >
            <IconTrash size={15} />
          </button>
          <IconChevronDown
            size={16}
            color="#9ca3af"
            style={{
              flexShrink: 0,
              transition: 'transform 0.2s ease',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
          />
        </div>
      </div>

      {/* 可收合內容 */}
      <Collapse expanded={expanded}>
        <div
          style={{
            borderTop:
              colorScheme === 'light'
                ? '1px solid #f3f4f6'
                : '1px solid #737373ff',
            padding: '14px 18px',
          }}
        >
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
            各時段費率
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(max-content, 1fr))',
              gap: 10,
              overflowX: 'auto',
            }}
          >
            {(() => {
              const now = new Date();
              const todayDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
              const nowTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
              return buildSpecificDayCards(rule).map((card, i) => {
                const isToday = card.dates.includes(todayDateStr);
                const activeSlotIndex = isToday
                  ? card.slots.findIndex(
                      (s) => s.startTime <= nowTime && nowTime <= s.endTime,
                    )
                  : -1;
                const activeMaxFeeIndex = isToday
                  ? card.maxFeeSlots.findIndex(
                      (s) => s.startTime <= nowTime && nowTime <= s.endTime,
                    )
                  : -1;
                return (
                  <SpecificRatePeriodCard
                    key={i}
                    dates={card.dates}
                    slots={card.slots}
                    maxFeeSlots={card.maxFeeSlots}
                    isToday={isToday}
                    isActiveRule={isCurrentlyActive}
                    activeSlotIndex={activeSlotIndex}
                    activeMaxFeeIndex={activeMaxFeeIndex}
                  />
                );
              });
            })()}
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
              進階費率資訊
            </div>
            <div
              style={{
                backgroundColor:
                  colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: colorScheme === 'light' ? '#374151' : 'white',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}
            >
              {formatAdditionalRules(rule.additionalRules)}
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
              費率備註
            </div>
            <div
              style={{
                backgroundColor:
                  colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: colorScheme === 'light' ? '#374151' : 'white',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}
            >
              {rule.description || '-'}
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

// ─── Main modal component ─────────────────────────────────────────────────────

interface ParkingRateOverviewModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  onNavigateToEdit: (
    ruleIndex: number,
    ruleType: 'regular' | 'specific',
  ) => void;
  onNavigateToAdd: () => void;
  onNavigateToCopy: (
    rule: RegularRule | SpecificRule,
    ruleType: 'regular' | 'specific',
  ) => void;
}

export const ParkingRateOverviewModal = ({
  opened,
  onClose,
  serviceId,
  onNavigateToEdit,
  onNavigateToAdd,
  onNavigateToCopy,
}: ParkingRateOverviewModalProps) => {
  const { data: service, isLoading } = useParkingService(serviceId);
  const { mutateAsync: updateParkingRates } =
    useUpdateParkingServiceParkingRates();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<FilterType>('all');
  const [isDeletingRule, setIsDeletingRule] = useState(false);

  const parkingRate = service?.parkingRate;
  const regularRules = parkingRate?.regularRules ?? [];
  const specificRules = parkingRate?.specificRules ?? [];

  // 優先採用 specificRule：若當下有 active 的 specificRule，regularRule 一律不 active
  const activeSpecificIdx = specificRules.findIndex(isRuleActive);
  const activeRegularIdx =
    activeSpecificIdx !== -1 ? -1 : regularRules.findIndex(isRuleActive);

  const openEditPage = (
    ruleIndex: number,
    ruleType: 'regular' | 'specific',
  ) => {
    onClose();
    onNavigateToEdit(ruleIndex, ruleType);
  };

  const openAddPage = () => {
    onClose();
    onNavigateToAdd();
  };

  const openCopyPage = (
    rule: RegularRule | SpecificRule,
    ruleType: 'regular' | 'specific',
  ) => {
    onClose();
    onNavigateToCopy(rule, ruleType);
  };

  const handleDeleteRule = (
    ruleIndex: number,
    ruleType: 'regular' | 'specific',
  ) => {
    const ruleName =
      ruleType === 'regular'
        ? regularRules[ruleIndex]?.name
        : specificRules[ruleIndex]?.name;

    const confirmId = modals.openConfirmModal({
      title: (
        <Text
          style={{
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          刪除費率
        </Text>
      ),
      children: (
        <Text
          style={{
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {`是否確認刪除「${ruleName}」費率？`}
        </Text>
      ),
      centered: true,
      labels: {
        confirm: '確認',
        cancel: '取消',
      },
      confirmProps: { color: 'blue' },

      onConfirm: async () => {
        modals.close(confirmId);
        setIsDeletingRule(true);
        try {
          const newRegularRules =
            ruleType === 'regular'
              ? regularRules.filter((_, i) => i !== ruleIndex)
              : [...regularRules];
          const newSpecificRules =
            ruleType === 'specific'
              ? specificRules.filter((_, i) => i !== ruleIndex)
              : [...specificRules];

          await updateParkingRates({
            serviceId,
            parkingRate: {
              regularRules: newRegularRules,
              specificRules: newSpecificRules,
            },
          });
          await queryClient.invalidateQueries({
            queryKey: ['parkingServices', serviceId],
          });
          notifySuccess('刪除停車費率成功');
        } catch {
          notifyError('刪除失敗，請稍後再試');
        } finally {
          setIsDeletingRule(false);
        }
      },
    });
  };

  const { colorScheme } = useTheme();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="費率管理"
      centered
      closeOnClickOutside={false}
      size="60%"
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        content: { minWidth: 1040 },
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '80vh',
          fontFamily: "'Segoe UI', system-ui, sans-serif",
          position: 'relative',
        }}
      >
        <LoadingOverlay visible={isLoading || isDeletingRule} />

        {/* Filter */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '4px 0 16px',
            flexShrink: 0,
          }}
        >
          <SegmentedControl
            value={filter}
            onChange={(v) => setFilter(v as FilterType)}
            data={[
              {
                value: 'all',
                label: `全部 (${regularRules.length + specificRules.length})`,
              },
              { value: 'regular', label: `一般費率 (${regularRules.length})` },
              {
                value: 'specific',
                label: `特殊費率 (${specificRules.length})`,
              },
            ]}
          />
        </div>

        {/* Rule cards (scrollable) */}
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
          {/* Empty state */}
          {regularRules.length === 0 && specificRules.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '64px 16px',
                color: '#9ca3af',
                fontSize: 13,
                lineHeight: 1.8,
              }}
            >
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              尚未設定任何費率
              <br />
              點擊「新增費率」開始設定
            </div>
          )}

          {/* 排列順序：active specific → active regular → 其餘 specific → 其餘 regular */}
          {(() => {
            type RuleItem =
              | {
                  kind: 'regular';
                  rule: RegularRule;
                  idx: number;
                  active: boolean;
                }
              | {
                  kind: 'specific';
                  rule: SpecificRule;
                  idx: number;
                  active: boolean;
                };

            const items: RuleItem[] = [
              ...(filter === 'all' || filter === 'specific'
                ? specificRules.map((rule, idx) => ({
                    kind: 'specific' as const,
                    rule,
                    idx,
                    active: idx === activeSpecificIdx,
                  }))
                : []),
              ...(filter === 'all' || filter === 'regular'
                ? regularRules.map((rule, idx) => ({
                    kind: 'regular' as const,
                    rule,
                    idx,
                    active: idx === activeRegularIdx,
                  }))
                : []),
            ].sort((a, b) => {
              // regular 優先於 specific
              if (a.kind !== b.kind) return a.kind === 'regular' ? -1 : 1;
              // 同類型按開始日期降序（最晚的在最前面）
              return b.rule.effectivePeriod.startDateTime.localeCompare(
                a.rule.effectivePeriod.startDateTime,
              );
            });

            return items.map((item) =>
              item.kind === 'regular' ? (
                <RegularRuleCard
                  key={`regular-${item.idx}`}
                  isCurrentlyActive={item.active}
                  rule={item.rule}
                  onEdit={() => openEditPage(item.idx, 'regular')}
                  onCopy={() => openCopyPage(item.rule, 'regular')}
                  onDelete={() => handleDeleteRule(item.idx, 'regular')}
                />
              ) : (
                <SpecificRuleCard
                  key={`specific-${item.idx}`}
                  isCurrentlyActive={item.active}
                  rule={item.rule}
                  onEdit={() => openEditPage(item.idx, 'specific')}
                  onCopy={() => openCopyPage(item.rule, 'specific')}
                  onDelete={() => handleDeleteRule(item.idx, 'specific')}
                />
              ),
            );
          })()}
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop:
              colorScheme === 'light'
                ? '1px solid #e5e7eb'
                : '1px solid #737373ff',
            paddingTop: 16,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <Button variant="default" onClick={onClose}>
            關閉
          </Button>
          <Button onClick={openAddPage}>+ 新增費率</Button>
        </div>
      </div>
    </Modal>
  );
};
